import React, { useState } from 'react';
import { Dialog } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { Button } from '../../components/Button';
import Input from '../../components/Input';
import InputMask from '../../components/InputMask';
import { Checkbox } from '../../components/Checkbox';
import logoImg from '../../assets/CEDAPP-CRM.png';
import { useAuth } from '../../hooks/auth';
import { useLoader } from '../../hooks/loading';
import {
  apiPhato,
  authApi,
  CEDAPP_API_KEY,
} from '../../services/api';
import { VerificationCodeModal } from './Modal';
import { RegisterPhone } from './RegisterPhone';
import { createPhatoAccessContext } from './phato-access-context';
import {
  buildPhonePayload,
  getPhoneEndpoint,
  getSuccessfulApiNames,
  isAdminLogin,
  LoginApiName,
} from './login-flow';
import { Container, Content, Logo, Title } from './styles';

interface LoginObjectProps {
  login: string;
  password: string;
  isMedico: boolean;
}

export function SignIn() {
  const history = useHistory();
  const [loginObject, setLoginObject] = useState<LoginObjectProps>({
    isMedico: true,
    login: '',
    password: '',
  });
  const [verificationCode, setVerificationCode] = useState(false);
  const [registerPhone, setRegisterPhone] = useState(false);
  const {
    loadStorageData,
    setPhatoAccess,
    signInPhato,
    signInLaureano,
    signInLaureanoSoul,
    signInHnsn,
  } = useAuth();
  const { showLoader, hideLoader } = useLoader();

  function getSessionCredentials() {
    return {
      login: loginObject.login,
      password: loginObject.password,
      isMedico: loginObject.isMedico,
    };
  }

  async function finishLogin() {
    showLoader();

    try {
      const credentials = getSessionCredentials();
      const requestNames: LoginApiName[] = [
        'phato',
        'laureano',
        'laureanoSoul',
        'hnsn',
      ];
      const requests: Array<Promise<unknown>> = [
        signInPhato(credentials),
        signInLaureano(credentials),
        signInLaureanoSoul(credentials),
        signInHnsn(credentials),
      ];

      const results = await Promise.allSettled(requests);
      const successfulApis = getSuccessfulApiNames(results, requestNames);

      if (successfulApis.includes('phato')) {
        await setPhatoAccess(createPhatoAccessContext(loginObject));
      }

      if (successfulApis.length === 0) {
        alert('Usuário ou senha inválidos');
        return;
      }

      await loadStorageData();
      history.push('/home');
    } finally {
      hideLoader();
    }
  }

  async function getPhatoPhoneStatus() {
    const normalizedLogin = loginObject.login.replace(/\D/g, '');
    try {
      const response = await apiPhato.get(
        getPhoneEndpoint(
          loginObject.isMedico,
          normalizedLogin,
          'exists',
        ),
        {
          headers: { 'x-api-key': CEDAPP_API_KEY },
          validateStatus: () => true,
        },
      );

      if (response.status === 204) return 'missing' as const;
      if (response.status >= 200 && response.status < 300) {
        return 'exists' as const;
      }
    } catch (error) {
      return 'unavailable' as const;
    }

    return 'unavailable' as const;
  }

  async function handleGenerateCode() {
    try {
      await authApi.post(
        '/auth-tokens',
        {
          login: loginObject.login.replace(/\D/g, ''),
          password: loginObject.password,
          isDoctor: loginObject.isMedico,
        },
        { silentErrorToast: true },
      );
      setVerificationCode(true);
    } catch (error) {
      alert('Não foi possível enviar o código de verificação.');
    }
  }

  async function preSignIn() {
    if (!loginObject.login || !loginObject.password) {
      alert('Preencha os dados para fazer login!');
      return;
    }

    if (isAdminLogin(loginObject.login)) {
      await finishLogin();
      return;
    }

    showLoader();
    try {
      const phoneStatus = await getPhatoPhoneStatus();

      if (phoneStatus === 'missing') {
        setRegisterPhone(true);
        return;
      }

      if (phoneStatus === 'exists') {
        await handleGenerateCode();
        return;
      }

      await finishLogin();
    } finally {
      hideLoader();
    }
  }

  async function handleSendPhone(phone: string) {
    const onlyNumbers = phone.replace(/\D/g, '');
    const ddd = onlyNumbers.substring(0, 2);
    const number = onlyNumbers.substring(2);
    if (ddd.length !== 2 || number.length < 8) {
      alert('Telefone inválido. Verifique o DDD e o número.');
      return;
    }

    showLoader();
    try {
      await apiPhato.post(
        getPhoneEndpoint(
          loginObject.isMedico,
          loginObject.login.replace(/\D/g, ''),
          'register',
        ),
        buildPhonePayload({
          isMedico: loginObject.isMedico,
          login: loginObject.login,
          password: loginObject.password,
          ddd,
          number,
        }),
        { headers: { 'x-api-key': CEDAPP_API_KEY } },
      );

      setRegisterPhone(false);
      await handleGenerateCode();
    } catch (error) {
      alert('Não foi possível cadastrar o telefone. Tente novamente.');
    } finally {
      hideLoader();
    }
  }

  async function handleSkipPhone() {
    setRegisterPhone(false);
    await finishLogin();
  }

  async function handleVerifyCode(code: string) {
    if (!code) {
      alert('Código de verificação inválido');
      return;
    }

    try {
      await authApi.post(
        '/validate-auth-tokens',
        {
          login: loginObject.login.replace(/\D/g, ''),
          token: code,
        },
        { silentErrorToast: true },
      );
    } catch (error) {
      alert('Código de verificação inválido');
      return;
    }

    setVerificationCode(false);
    await finishLogin();
  }

  return (
    <Container>
      <Logo src={logoImg} alt="CEDAPP" />
      <div>
        <Title>Faça seu login</Title>
      </div>

      <Content>
        <div>
          <Checkbox
            onClick={(checked) =>
              setLoginObject((previous) => ({
                ...previous,
                isMedico: checked,
              }))
            }
            label="Acesso médico"
            checked={loginObject.isMedico}
          />
          <Checkbox
            onClick={(checked) =>
              setLoginObject((previous) => ({
                ...previous,
                isMedico: !checked,
              }))
            }
            label="Acesso paciente"
            checked={!loginObject.isMedico}
          />
        </div>

        {loginObject.isMedico ? (
          <>
            <Input
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  login: value,
                }));
              }}
              placeholder="CRM"
              value={loginObject.login}
            />
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  password: value,
                }));
              }}
              value={loginObject.password}
              placeholder="CPF"
              mask="999.999.999-99"
            />
          </>
        ) : (
          <>
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  login: value,
                }));
              }}
              value={loginObject.login}
              placeholder="CPF"
              mask="999.999.999-99"
            />
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  password: value,
                }));
              }}
              value={loginObject.password}
              placeholder="Data de nascimento"
              mask="99/99/9999"
            />
          </>
        )}

        <Dialog
          open={verificationCode}
          onClose={() => setVerificationCode(false)}
          PaperProps={{ style: { background: 'transparent' } }}
        >
          <VerificationCodeModal
            handleCloseModal={() => setVerificationCode(false)}
            handleSendVerificationCode={handleVerifyCode}
          />
        </Dialog>
        <Dialog
          open={registerPhone}
          onClose={handleSkipPhone}
          PaperProps={{ style: { background: 'transparent' } }}
        >
          <RegisterPhone
            handleCloseModal={handleSkipPhone}
            handleSendPhone={handleSendPhone}
          />
        </Dialog>

        <Button onClick={preSignIn}>Entrar</Button>
      </Content>
    </Container>
  );
}
